import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  ListFileEditForm,
  ListFileEditInitialValues,
  ListFileComponents,
  ListFileAdditionalParams,
  ListFileFormOptions,
  onListFileFormSaved,
  IListFileFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedListFile, ISharedPatchListFile, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchListFile, patchListFile, getListFileUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useLists, useFields, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedListFile> {}

const ListFileFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const lists = useLists();
  const fields = useFields();

  useEffect(() => {
    fetchListFile(id).then(list_file => setValue(list_file));
  }, [id, setValue, ]);

  const param_props: IListFileFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    lists: lists.lists,
    fields: fields.fields,
  };
  const formLayout: IForm | undefined = useMemo(() => value && ListFileEditForm(value, param_props, t), [value, organizations, lists, fields, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && ListFileFormOptions(value, param_props, t), [value, organizations, lists, fields, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchListFile, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchListFile) => value && save(() => patchListFile(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onListFileFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !lists.loaded || !fields.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.List, value)}
    >
      <AtlasForm<ISharedPatchListFile>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={ListFileComponents}
        additionalParams={ListFileAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={ListFileEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('LIST_FILE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const ListFileFormEditView = React.memo(withEdit(ListFileFormEdit, getListFileUpdateSchema));
